/**
 * Canonical in-app notification vocabulary (DD.9.5.1), shared by the
 * notification writers (server) and the bell UI (client) so category
 * + type strings can't drift between the two surfaces.
 *
 * The `user_notification` table stores these as free-form strings (no
 * DB enum) so new values can land without a migration — this file is
 * the coordination point the entity comment refers to.
 *
 * NOTE: `apps/crm-api/.../user-notification-writer.service.ts` still
 * carries a local `USER_NOTIFICATION_CATEGORIES` mirror of the
 * categories below. Phase B (wiring the real producers) points that at
 * this canonical source; until then the two are kept identical by hand.
 */
export const NOTIFICATION_CATEGORY = {
	PHONE: 'phone',
	SALES: 'sales',
	APPROVALS: 'approvals',
	FINANCIAL: 'financial',
	COLLABORATION: 'collaboration',
	OPERATIONS: 'operations',
	SYSTEM: 'system',
	AI: 'ai',
} as const;

export type NotificationCategory =
	(typeof NOTIFICATION_CATEGORY)[keyof typeof NOTIFICATION_CATEGORY];

/**
 * Known notification "type" verbs — the specific event each row
 * describes. The render layer maps these to an icon (falling back to a
 * category-level icon for unknown verbs). Free-form at the DB layer;
 * enumerated here so producers and the bell's icon map stay aligned.
 *
 * BELL vs COMMUNICATIONS-PANEL BOUNDARY (PD-14298, see
 * docs/claude/notifications/ADR-bell-vs-communications-boundary.md):
 * `phone`-category signals (voicemail, the live missed-call list) and ambient
 * unread chat are owned by the Communications panel + chat nav dot, NOT the
 * bell — so `voicemail_arrived` and an "unread chat" verb are intentionally
 * absent here. The ONLY phone/collaboration events that belong on the bell are
 * the addressed / "missed while away" escalations: `missed_call`, `mention`,
 * `missed_chat`, and `task_assigned`. `NotificationDispatchService` enforces
 * this at dispatch time.
 */
export const NOTIFICATION_TYPE = {
	// Watcher / owner entity changes (PD-14291). Each entity is its OWN
	// type so a user can turn each on/off independently in preferences,
	// even though they share the watcher pipeline.
	CLIENT_UPDATED: 'client_updated',
	OPPORTUNITY_UPDATED: 'opportunity_updated',
	ISSUE_UPDATED: 'issue_updated',
	// PD-16475 — a new issue: the batch that carries the issue's creation is
	// sent as this type instead of ISSUE_UPDATED, so a sales rep can keep
	// "new issue for my client" while muting the stream of later edits.
	ISSUE_CREATED: 'issue_created',
	LICENSE_UPDATED: 'license_updated',
	SOURCING_REQUEST_UPDATED: 'sourcing_request_updated',
	// Sales intelligence
	COMPETITIVE_PRICE_CHANGED: 'competitive_price_changed',
	// PD-16251 — an item cost change removed, raised or lowered saved prices of
	// the salesperson's customers, under the tenant's saved-price policy.
	SAVED_PRICE_COST_CHANGED: 'saved_price_cost_changed',
	ABANDONED_CART_ROUNDUP: 'abandoned_cart_roundup',
	LEAD_REGISTERED: 'lead_registered',
	// PD-16721 — a storefront user deleted their own login; sent to the
	// salesperson assigned to each account the login could reach.
	PORTAL_ACCOUNT_DELETED: 'portal_account_deleted',
	// Workd Forms (PD-16572): a web form submission to act on, and an action that
	// failed after its retries.
	FORM_SUBMITTED: 'form_submitted',
	FORM_ACTION_FAILED: 'form_action_failed',
	// PD-16686 — a form submission was assigned to you by hand in the inbox. Its own
	// type, apart from FORM_SUBMITTED, so a person can keep "assigned to me" while
	// muting the stream of new submissions.
	FORM_SUBMISSION_ASSIGNED: 'form_submission_assigned',
	// PD-13768 — a customer completed and signed a form a staff member sent them; the
	// bell goes to the sender.
	SENT_FORM_COMPLETED: 'sent_form_completed',
	// PD-17107 — a colleague shared a visit debrief with you (or your team).
	VISIT_DEBRIEF_SHARED: 'visit_debrief_shared',
	// PD-16841 — an order was placed for the salesperson's client by someone
	// else (a colleague in the CRM, or the customer on the storefront). Its own
	// type so a rep can turn the email off; it was sent to everyone with no
	// setting before.
	ORDER_SUBMITTED_FOR_CLIENT: 'order_submitted_for_client',
	// Approvals
	ORDER_APPROVAL_REQUIRED: 'order_approval_required',
	PURCHASE_ORDER_APPROVAL_REQUIRED: 'purchase_order_approval_required',
	PURCHASE_ORDER_APPROVAL_DECISION: 'purchase_order_approval_decision',
	// Assignment / collaboration
	ISSUE_ASSIGNED: 'issue_assigned',
	TASK_ASSIGNED: 'task_assigned',
	// Distinct from TASK_ASSIGNED (assignment) — the daily due-date /
	// follow-up reminder for an open task that is due today or overdue
	// (PD-14305). Its own type so a user can mute the recurring "task due"
	// nag without losing the one-shot "you got assigned a task" alert.
	TASK_DUE: 'task_due',
	MENTION: 'mention',
	// A chat message arrived while the recipient was offline (PD-14300).
	// The live @-mention bell already covers present-or-mentioned users;
	// this fills the gap for someone who simply wasn't there to see it.
	MISSED_CHAT: 'missed_chat',
	// An inbound call rang a rep who didn't answer (PD-14300).
	MISSED_CALL: 'missed_call',
	// Financial / system
	CARD_PAYMENT_FAILED: 'card_payment_failed',
	INVOICE_OVERDUE: 'invoice_overdue',
	PAYMENT_RECEIVED: 'payment_received',
	// PD-16674 — a customer saved a payment method through a link a staff
	// member sent them; the bell goes to that staff member.
	PAYMENT_METHOD_LINK_USED: 'payment_method_link_used',
	SYNC_FAILED: 'sync_failed',
	// A phone-number port-in completed and the number is now active (PD-14264).
	// Categorized OPERATIONS, NOT phone: it's an account/provisioning event, not
	// call/message traffic, so it's a legitimate bell notification (the `phone`
	// category is Communications-panel-owned and would trip the bell boundary).
	PORT_IN_CUTOVER_COMPLETE: 'port_in_cutover_complete',
	// PD-14306 — an item the user follows is available again. Sent by the
	// scheduled back-in-stock sweep to each follower, once per restock.
	ITEM_BACK_IN_STOCK: 'item_back_in_stock',
} as const;

export type NotificationType =
	(typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE];

/** Sort weight + visual emphasis (mirrors the entity column union). */
export type NotificationPriority = 'urgent' | 'normal' | 'info';

/**
 * Delivery channels a notification can fan out to (PD-14290). The
 * per-user preference model (`user_notification_preference`) carries a
 * boolean per channel, and the dispatcher resolves each channel
 * independently before delivering.
 *
 *   - `IN_APP`  — the notification bell tray (a `user_notification` row).
 *   - `EMAIL`   — transactional email via EmailService.
 *   - `PUSH`    — browser / mobile push (overridable per notificationType).
 */
export const NOTIFICATION_CHANNEL = {
	IN_APP: 'in_app',
	EMAIL: 'email',
	PUSH: 'push',
} as const;

export type NotificationChannel =
	(typeof NOTIFICATION_CHANNEL)[keyof typeof NOTIFICATION_CHANNEL];

/**
 * One notifiable event in the preferences catalog (PD-14290/14292). The
 * preference grain is per EVENT TYPE — each event is individually
 * toggleable per channel, even when several events share the same
 * underlying pipeline (e.g. client / opportunity / sourcing-request all
 * flow through the watcher handler but each appears as its own row).
 *
 *   - `category` groups events under a heading in the preferences UI and
 *     is also stored on the `user_notification` row for the bell icon.
 *   - `defaults` are applied when a user has no explicit preference row,
 *     chosen to PRESERVE prior behavior (watcher/approval events default
 *     email-on; the new bell-only events default email-off; push is
 *     opt-in everywhere until the push transport ships).
 */
export interface NotificationEventDefModel {
	type: NotificationType;
	category: NotificationCategory;
	/** Human label for the preferences row (wrapped with t() on render). */
	label: string;
	defaults: { inApp: boolean; email: boolean; push: boolean };
}

const ON_BELL_EMAIL = { inApp: true, email: true, push: false };
const ON_BELL_ONLY = { inApp: true, email: false, push: false };
const ON_EMAIL_ONLY = { inApp: false, email: true, push: false };

/**
 * THE catalog of user-manageable notification events — the single
 * source of truth for the preferences matrix rows AND the resolver's
 * per-event default channels. Add a new producer's type here to make it
 * appear (and be controllable) in every user's Notification Preferences.
 */
export const NOTIFICATION_EVENTS: ReadonlyArray<NotificationEventDefModel> = [
	// Watcher / owner — separate row per entity (each independently togglable)
	{
		type: NOTIFICATION_TYPE.CLIENT_UPDATED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'Client changes (you watch or own)',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.OPPORTUNITY_UPDATED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'Opportunity changes (you watch or own)',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.COMPETITIVE_PRICE_CHANGED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: "Competitive price changed (your client's item)",
		defaults: ON_BELL_ONLY,
	},
	{
		// PD-16251 — whether this email is sent at all is the tenant's
		// saved-price policy (Email the salesperson); this lets a rep turn
		// their own copy off.
		type: NOTIFICATION_TYPE.SAVED_PRICE_COST_CHANGED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'Saved prices changed by an item cost change (your customers)',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.ABANDONED_CART_ROUNDUP,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'Abandoned carts to follow up',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.LEAD_REGISTERED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'New lead registered',
		defaults: ON_BELL_EMAIL,
	},
	{
		// PD-16721 — bell + email by default: the rep may want to follow up or set
		// up a new login for the customer.
		type: NOTIFICATION_TYPE.PORTAL_ACCOUNT_DELETED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'Customer deleted their storefront login (your client)',
		defaults: ON_BELL_EMAIL,
	},
	{
		// PD-16841 — email-only by default, exactly what was sent before the
		// setting existed. The bell is opt-in: an order a rep did not place
		// is common enough on a busy account that a bell entry per order
		// would crowd out the ones that need action.
		type: NOTIFICATION_TYPE.ORDER_SUBMITTED_FOR_CLIENT,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'Order placed for your client by someone else',
		defaults: ON_EMAIL_ONLY,
	},
	{
		type: NOTIFICATION_TYPE.FORM_SUBMITTED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'Web form submitted (you are told about it)',
		defaults: ON_BELL_EMAIL,
	},
	{
		// PD-16686 — someone assigned you a web form submission by hand. Bell + email
		// by default, like FORM_SUBMITTED: a submission is a visitor waiting on a reply,
		// and the email carries the link to it. Assignments made automatically when a
		// submission arrives are announced by the form's notify action instead.
		type: NOTIFICATION_TYPE.FORM_SUBMISSION_ASSIGNED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'Form submission assigned to you',
		defaults: ON_BELL_EMAIL,
	},
	{
		// PD-13768 — the sender of a form learns the customer signed it, with the link to
		// the submission to process. Bell only by default: the signed PDF is already in the
		// client's Files and the submission is in the inbox.
		type: NOTIFICATION_TYPE.SENT_FORM_COMPLETED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'A customer signed a form you sent',
		defaults: ON_BELL_ONLY,
	},
	{
		// PD-17107 — a colleague shared a visit debrief with you or a team you are
		// on. Bell + email by default: sharing is the sender asking you to read it,
		// and the email carries the link to the visit.
		type: NOTIFICATION_TYPE.VISIT_DEBRIEF_SHARED,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'A visit debrief was shared with you',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.FORM_ACTION_FAILED,
		category: NOTIFICATION_CATEGORY.SYSTEM,
		label: 'A web form action failed',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.SOURCING_REQUEST_UPDATED,
		category: NOTIFICATION_CATEGORY.OPERATIONS,
		label: 'Sourcing request changes (you watch or own)',
		defaults: ON_BELL_EMAIL,
	},
	{
		// PD-16475 — same recipients as issue changes (the client's sales rep,
		// the assignee, watchers and anyone routed by the admin settings), sent
		// once when the issue is created.
		type: NOTIFICATION_TYPE.ISSUE_CREATED,
		category: NOTIFICATION_CATEGORY.OPERATIONS,
		label: 'New issue for your client (or assigned to you)',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.ISSUE_UPDATED,
		category: NOTIFICATION_CATEGORY.OPERATIONS,
		label: 'Issue changes (you watch)',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.LICENSE_UPDATED,
		category: NOTIFICATION_CATEGORY.OPERATIONS,
		label: 'License changes (you watch or own)',
		defaults: ON_BELL_EMAIL,
	},
	// Approvals
	{
		type: NOTIFICATION_TYPE.ORDER_APPROVAL_REQUIRED,
		category: NOTIFICATION_CATEGORY.APPROVALS,
		label: 'Order needs your approval',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.PURCHASE_ORDER_APPROVAL_REQUIRED,
		category: NOTIFICATION_CATEGORY.APPROVALS,
		label: 'Purchase order needs your approval',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.PURCHASE_ORDER_APPROVAL_DECISION,
		category: NOTIFICATION_CATEGORY.APPROVALS,
		label: 'Your purchase order was approved / rejected',
		defaults: ON_BELL_EMAIL,
	},
	// Assignment / collaboration (new bell-first events — email off by default)
	{
		type: NOTIFICATION_TYPE.ISSUE_ASSIGNED,
		category: NOTIFICATION_CATEGORY.OPERATIONS,
		label: 'Issue assigned to you',
		defaults: ON_BELL_ONLY,
	},
	{
		type: NOTIFICATION_TYPE.TASK_ASSIGNED,
		category: NOTIFICATION_CATEGORY.COLLABORATION,
		label: 'Task assigned to you',
		defaults: ON_BELL_ONLY,
	},
	{
		// Daily due-date / follow-up reminder (PD-14305). SALES category (not
		// COLLABORATION like the assignment alert above) so it groups with the
		// rep's sales-activity reminders; bell-only by default since a daily
		// recurring nag defaulting email-on would be inbox spam.
		type: NOTIFICATION_TYPE.TASK_DUE,
		category: NOTIFICATION_CATEGORY.SALES,
		label: 'Task due / overdue reminder',
		defaults: ON_BELL_ONLY,
	},
	{
		type: NOTIFICATION_TYPE.MENTION,
		category: NOTIFICATION_CATEGORY.COLLABORATION,
		label: 'You were @mentioned',
		defaults: ON_BELL_ONLY,
	},
	{
		// Bell-only by default: missed chats are high-frequency, so an
		// email per offline message would be noise (mirrors @mention).
		type: NOTIFICATION_TYPE.MISSED_CHAT,
		category: NOTIFICATION_CATEGORY.COLLABORATION,
		label: 'Chat message you missed (you were offline)',
		defaults: ON_BELL_ONLY,
	},
	// Phone
	{
		// Bell + email by default: a missed call is durable and
		// lower-frequency, worth surfacing in email too.
		type: NOTIFICATION_TYPE.MISSED_CALL,
		category: NOTIFICATION_CATEGORY.PHONE,
		label: 'Missed call',
		defaults: ON_BELL_EMAIL,
	},
	// Financial / system
	{
		type: NOTIFICATION_TYPE.CARD_PAYMENT_FAILED,
		category: NOTIFICATION_CATEGORY.FINANCIAL,
		label: "Card payment failed (your client's order)",
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.INVOICE_OVERDUE,
		category: NOTIFICATION_CATEGORY.FINANCIAL,
		label: 'Invoice overdue',
		defaults: ON_BELL_EMAIL,
	},
	{
		type: NOTIFICATION_TYPE.PAYMENT_RECEIVED,
		category: NOTIFICATION_CATEGORY.FINANCIAL,
		label: 'Payment received',
		defaults: ON_BELL_ONLY,
	},
	{
		// PD-16674 — bell-only by default, like a received payment: the sender
		// is waiting on it, and the card itself is visible on the client.
		type: NOTIFICATION_TYPE.PAYMENT_METHOD_LINK_USED,
		category: NOTIFICATION_CATEGORY.FINANCIAL,
		label: 'Customer saved a payment method from your link',
		defaults: ON_BELL_ONLY,
	},
	{
		type: NOTIFICATION_TYPE.SYNC_FAILED,
		category: NOTIFICATION_CATEGORY.SYSTEM,
		label: 'Integration sync failed',
		defaults: ON_BELL_ONLY,
	},
	{
		// Bell + email by default: a port-in cutover is a durable, infrequent,
		// "your phone numbers are now live" event the tenant wants to know about.
		type: NOTIFICATION_TYPE.PORT_IN_CUTOVER_COMPLETE,
		category: NOTIFICATION_CATEGORY.OPERATIONS,
		label: 'Phone number port-in completed',
		defaults: ON_BELL_EMAIL,
	},
	{
		// PD-14306 — bell + email by default: following an item is an explicit
		// request to be told, and the restock often happens while the follower
		// is away from the app.
		type: NOTIFICATION_TYPE.ITEM_BACK_IN_STOCK,
		category: NOTIFICATION_CATEGORY.OPERATIONS,
		label: 'Item you follow is back in stock',
		defaults: ON_BELL_EMAIL,
	},
];

/** Default channel decision for a type+channel when the user has no row. */
export function defaultChannelForType(
	notificationType: string,
	channel: NotificationChannel,
): boolean {
	const def = NOTIFICATION_EVENTS.find((e) => e.type === notificationType);
	// Unknown/unlisted type: show in-app, nothing else (safe fallback).
	const defaults = def?.defaults ?? { inApp: true, email: false, push: false };
	switch (channel) {
		case NOTIFICATION_CHANNEL.IN_APP:
			return defaults.inApp;
		case NOTIFICATION_CHANNEL.EMAIL:
			return defaults.email;
		case NOTIFICATION_CHANNEL.PUSH:
			return defaults.push;
		default:
			return false;
	}
}
